import { useEffect, useRef, useState } from 'react';
import type { IReplayEvent } from '@interfaces/IMonitorApi';
import { useAppSelector } from '@store/hooks';
import { selectSafetyEvents } from '@store/selectors/monitor.selectors';
import { useSafetyVisible } from '../hooks/useSafetyVisible';
import type { Anchor } from '../MainView/buildingViewAnchors';
import { ReplayEventLayer } from './ReplayEventLayer';
import { EVENT_SHOW_MS } from './useActiveEvents';

type SafetyEvents = ReturnType<typeof selectSafetyEvents>;
type LiveEvent = Extract<IReplayEvent, { kind: 'safety' }>;

const NO_EQUIPMENT = {};
/** A burst (a reconnect, a bad minute) must not bury the building in pop-ups. */
const MAX_SHOWN = 3;
/** Room for the site server's clock running ahead of or behind this browser's. */
const CLOCK_SKEW_MS = 60_000;

const toLiveEvent = (event: SafetyEvents[number]): LiveEvent => ({
  kind: 'safety',
  at: new Date(event.timestampCreated).toISOString(),
  eventId: event._id,
  floorId: event.floorId ?? null,
  isCritical: event.isCritical,
  typeKey: event.safetyViolationType,
  trade: event.trade,
});

/** The safety alerts raised since this hook mounted, each kept for EVENT_SHOW_MS.
 *  A new alert replaces one on the same floor, whose pop-up it would cover.
 *  The stream's first batch can land after the page does: alerts raised before
 *  the page opened are history then, not news. */
function useLiveSafetyEvents(): readonly LiveEvent[] {
  const events = useAppSelector(selectSafetyEvents);
  const seen = useRef(new Set(events.map((event) => event._id)));
  const [since] = useState(() => Date.now() - CLOCK_SKEW_MS);
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());
  const [shown, setShown] = useState<readonly LiveEvent[]>([]);

  useEffect(() => {
    const fresh = events.filter((event) => !seen.current.has(event._id));
    for (const event of fresh) seen.current.add(event._id);
    // A stream event can be the close of one that fired before this mounted.
    const raised = fresh
      .filter((event) => event.status === 'active' && event.timestampCreated >= since)
      .map(toLiveEvent);
    if (raised.length === 0) return;
    setShown((list) => {
      const floors = new Set(raised.map((event) => event.floorId));
      return [...list.filter((event) => !floors.has(event.floorId)), ...raised].slice(-MAX_SHOWN);
    });
    const timer = setTimeout(() => {
      timers.current.delete(timer);
      setShown((list) => list.filter((event) => !raised.includes(event)));
    }, EVENT_SHOW_MS);
    timers.current.add(timer);
  }, [events, since]);

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach(clearTimeout);
  }, []);

  return shown;
}

interface ILiveEventLayerProps {
  /** The building's floor rows (`floor<id>` markers). */
  anchors: Anchor[];
}

/** New live safety alerts over the building, drawn as the replay draws its events. */
export function LiveEventLayer({ anchors }: ILiveEventLayerProps) {
  const visible = useSafetyVisible();
  const events = useLiveSafetyEvents();
  if (!visible) return null;
  return <ReplayEventLayer anchors={anchors} events={events} equipment={NO_EQUIPMENT} />;
}
